import { useEffect, useState } from "react";
import { api, errDetail, fmtEur, fmtDate } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Factory, Wallet, AlertTriangle, Clock, History, X } from "lucide-react";

const emptyComp = () => ({ name: "", share_pct: "", source: "" });

function SupplyDialog({ open, onClose, plants, initial, accountId, onSaved, t }) {
  const [f, setF] = useState(initial || { plant_id: "", period: "", period_type: "quarter", qty: "", unit: "Stk", revenue_eur: "" });
  useEffect(() => { setF(initial || { plant_id: "", period: "", period_type: "quarter", qty: "", unit: "Stk", revenue_eur: "" }); }, [initial, open]);
  const save = async () => {
    if (!f.plant_id || !f.period) { toast.error(t("ws_plant") + " / " + t("ws_period")); return; }
    try {
      if (f.id) await api.put(`/plant-supplies/${f.id}`, f);
      else await api.post(`/accounts/${accountId}/plant-supplies`, f);
      toast.success(t("ws_saved")); onSaved(); onClose();
    } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="supply-dialog">
        <DialogHeader><DialogTitle>{t("ws_plant_supply")}</DialogTitle><DialogDescription>{t("ws_add_supply")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">{t("ws_plant")}</Label>
            <Select value={f.plant_id} onValueChange={(v) => setF((x) => ({ ...x, plant_id: v }))}>
              <SelectTrigger data-testid="supply-plant-select"><SelectValue placeholder={t("ws_plant")} /></SelectTrigger>
              <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">{t("ws_period")}</Label>
              <Input placeholder="2026-Q1" value={f.period} onChange={(e) => setF((x) => ({ ...x, period: e.target.value }))} data-testid="supply-period-input" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("type")}</Label>
              <Select value={f.period_type} onValueChange={(v) => setF((x) => ({ ...x, period_type: v }))}>
                <SelectTrigger data-testid="supply-periodtype-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="month">{t("ws_period_month")}</SelectItem><SelectItem value="quarter">{t("ws_period_quarter")}</SelectItem></SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">{t("ws_qty")}</Label>
              <Input type="number" value={f.qty} onChange={(e) => setF((x) => ({ ...x, qty: e.target.value }))} data-testid="supply-qty-input" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("ws_unit")}</Label>
              <Input value={f.unit} onChange={(e) => setF((x) => ({ ...x, unit: e.target.value }))} data-testid="supply-unit-input" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("ws_revenue")}</Label>
              <Input type="number" value={f.revenue_eur} onChange={(e) => setF((x) => ({ ...x, revenue_eur: e.target.value }))} data-testid="supply-revenue-input" /></div>
          </div>
          <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="supply-save-btn">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WalletDialog({ open, onClose, initial, accountId, onSaved, t }) {
  const blank = { period: "", period_type: "quarter", category: "", total_budget_eur: "", our_share_eur: "", competitors: [], notes: "" };
  const [f, setF] = useState(initial || blank);
  useEffect(() => { setF(initial ? { ...initial, competitors: initial.competitors || [] } : blank); /* eslint-disable-next-line */ }, [initial, open]);
  const budget = Number(f.total_budget_eur) || 0;
  const ourPct = budget ? ((Number(f.our_share_eur) || 0) / budget * 100) : 0;
  const compPct = (f.competitors || []).reduce((s, c) => s + (Number(c.share_pct) || 0), 0);
  const totalPct = ourPct + compPct;
  const save = async () => {
    if (!f.period) { toast.error(t("ws_period")); return; }
    try {
      if (f.id) await api.put(`/wallet-shares/${f.id}`, f);
      else await api.post(`/accounts/${accountId}/wallet-shares`, f);
      toast.success(t("ws_saved")); onSaved(); onClose();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const setComp = (i, k, v) => setF((x) => ({ ...x, competitors: x.competitors.map((c, j) => j === i ? { ...c, [k]: v } : c) }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="wallet-dialog">
        <DialogHeader><DialogTitle>{t("ws_wallet")}</DialogTitle><DialogDescription>{t("ws_add_wallet")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">{t("ws_period")}</Label>
              <Input placeholder="2026-Q1" value={f.period} onChange={(e) => setF((x) => ({ ...x, period: e.target.value }))} data-testid="wallet-period-input" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("type")}</Label>
              <Select value={f.period_type} onValueChange={(v) => setF((x) => ({ ...x, period_type: v }))}>
                <SelectTrigger data-testid="wallet-periodtype-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="month">{t("ws_period_month")}</SelectItem><SelectItem value="quarter">{t("ws_period_quarter")}</SelectItem></SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-xs">{t("ws_category")}</Label>
              <Input value={f.category} onChange={(e) => setF((x) => ({ ...x, category: e.target.value }))} data-testid="wallet-category-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">{t("ws_budget")}</Label>
              <Input type="number" value={f.total_budget_eur} onChange={(e) => setF((x) => ({ ...x, total_budget_eur: e.target.value }))} data-testid="wallet-budget-input" /></div>
            <div className="space-y-1"><Label className="text-xs">{t("ws_our_eur")}</Label>
              <Input type="number" value={f.our_share_eur} onChange={(e) => setF((x) => ({ ...x, our_share_eur: e.target.value }))} data-testid="wallet-oureur-input" /></div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge className="bg-[#1B2A4A] text-white hover:bg-[#1B2A4A]">{t("ws_our_pct")}: {ourPct.toFixed(1)}%</Badge>
            <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">{t("ws_comp_pct")}: {compPct.toFixed(1)}%</Badge>
            <Badge className={totalPct > 100.5 ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}>{t("ws_total_pct")}: {totalPct.toFixed(1)}%</Badge>
          </div>
          {totalPct > 100.5 && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{t("ws_over100")}</p>}
          <div className="border-t pt-2">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs font-semibold">{t("ws_competitors")}</Label>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setF((x) => ({ ...x, competitors: [...x.competitors, emptyComp()] }))} data-testid="wallet-add-comp-btn"><Plus className="h-3 w-3 mr-1" />{t("ws_add_competitor")}</Button>
            </div>
            <div className="space-y-2">
              {(f.competitors || []).map((c, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5 bg-gray-50 rounded p-2">
                  <Input className="h-8 text-xs flex-1 min-w-[110px]" placeholder={t("ws_comp_name")} value={c.name} onChange={(e) => setComp(i, "name", e.target.value)} data-testid={`comp-name-${i}`} />
                  <Input className="h-8 text-xs w-20" type="number" placeholder="%" value={c.share_pct} onChange={(e) => setComp(i, "share_pct", e.target.value)} data-testid={`comp-share-${i}`} />
                  <Input className="h-8 text-xs flex-1 min-w-[120px]" placeholder={t("ws_comp_source")} value={c.source} onChange={(e) => setComp(i, "source", e.target.value)} data-testid={`comp-source-${i}`} />
                  <button className="text-gray-300 hover:text-red-500" onClick={() => setF((x) => ({ ...x, competitors: x.competitors.filter((_, j) => j !== i) }))}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">{t("ws_notes")}</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} data-testid="wallet-notes-input" /></div>
          <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="wallet-save-btn">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WalletShare({ accountId }) {
  const { t, hasIntel } = useApp();
  const [supplies, setSupplies] = useState([]);
  const [plants, setPlants] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [stale, setStale] = useState(false);
  const [supplyDlg, setSupplyDlg] = useState(null);
  const [walletDlg, setWalletDlg] = useState(null);
  const [histFor, setHistFor] = useState(null);

  const load = () => {
    api.get(`/accounts/${accountId}/plant-supplies`).then((r) => { setSupplies(r.data.supplies); setPlants(r.data.plants); }).catch(() => {});
    api.get(`/accounts/${accountId}/wallet-shares`).then((r) => { setWallets(r.data.wallet_shares); setStale(r.data.stale); }).catch(() => {});
  };
  useEffect(() => { if (hasIntel) load(); /* eslint-disable-next-line */ }, [accountId, hasIntel]);

  if (!hasIntel) return <p className="text-sm text-gray-400 py-8 text-center" data-testid="intel-denied">{t("access_denied_intel")}</p>;

  const delSupply = async (id) => { try { await api.delete(`/plant-supplies/${id}`); toast.success(t("ws_deleted")); load(); } catch (e) { toast.error(errDetail(e)); } };
  const delWallet = async (id) => { try { await api.delete(`/wallet-shares/${id}`); toast.success(t("ws_deleted")); load(); } catch (e) { toast.error(errDetail(e)); } };
  const chartData = wallets.map((w) => ({ period: w.period, our: w.our_share_pct, comp: w.competitor_share_pct }));

  return (
    <div className="space-y-5" data-testid="wallet-share-panel">
      {stale && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-800" data-testid="wallet-stale-hint">
          <Clock className="h-4 w-4" />{t("ws_stale")}
        </div>
      )}

      {/* Plant supplies */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><Factory className="h-4 w-4 text-[#1B2A4A]" />{t("ws_plant_supply")}</h3>
          <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A]" onClick={() => setSupplyDlg({})} data-testid="add-supply-btn"><Plus className="h-3.5 w-3.5 mr-1" />{t("ws_add_supply")}</Button>
        </div>
        {supplies.length === 0 ? <p className="text-sm text-gray-400 py-3">{t("ws_no_supply")}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b">
              <th className="py-2">{t("ws_plant")}</th><th>{t("ws_period")}</th><th className="text-right">{t("ws_qty")}</th><th>{t("ws_unit")}</th><th className="text-right">{t("ws_revenue")}</th><th></th>
            </tr></thead><tbody>
              {supplies.map((s) => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-amber-50/30" data-testid="supply-row">
                  <td className="py-2 font-medium">{s.plant_name}</td><td>{s.period}</td>
                  <td className="text-right font-mono">{Number(s.qty).toLocaleString("de-DE")}</td><td>{s.unit}</td>
                  <td className="text-right font-mono">{fmtEur(s.revenue_eur)}</td>
                  <td className="text-right whitespace-nowrap">
                    <button className="text-gray-300 hover:text-[#1B2A4A] mr-2" onClick={() => setSupplyDlg(s)} data-testid="edit-supply-btn"><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="text-gray-300 hover:text-red-500" onClick={() => delSupply(s.id)} data-testid="delete-supply-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>

      {/* Wallet share */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5"><Wallet className="h-4 w-4 text-[#1B2A4A]" />{t("ws_wallet")}</h3>
          <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A]" onClick={() => setWalletDlg({})} data-testid="add-wallet-btn"><Plus className="h-3.5 w-3.5 mr-1" />{t("ws_add_wallet")}</Button>
        </div>
        {chartData.length > 1 && (
          <div className="h-56 mb-4" data-testid="wallet-trend-chart">
            <p className="text-xs text-gray-400 mb-1">{t("ws_trend")}</p>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="our" name={t("ws_our_pct")} stroke="#1B2A4A" strokeWidth={2} />
                <Line type="monotone" dataKey="comp" name={t("ws_comp_pct")} stroke="#F5A623" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {wallets.length === 0 ? <p className="text-sm text-gray-400 py-3">{t("ws_no_wallet")}</p> : (
          <div className="space-y-2">
            {wallets.map((w) => (
              <div key={w.id} className="border border-gray-100 rounded-lg p-3" data-testid="wallet-row">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-medium">{w.period}</span>
                  {w.category && <Badge variant="outline" className="text-xs">{w.category}</Badge>}
                  <Badge className="bg-[#1B2A4A] text-white hover:bg-[#1B2A4A] text-xs">{t("ws_our_pct")}: {w.our_share_pct}%</Badge>
                  <Badge className={w.over_100 ? "bg-red-100 text-red-700 hover:bg-red-100 text-xs" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs"}>{t("ws_total_pct")}: {w.total_share_pct}%</Badge>
                  <span className="text-xs text-gray-400 ml-auto">{fmtEur(w.our_share_eur)} / {fmtEur(w.total_budget_eur)}</span>
                  <button className="text-gray-300 hover:text-gray-600" onClick={() => setHistFor(w)} data-testid="wallet-history-btn"><History className="h-3.5 w-3.5" /></button>
                  <button className="text-gray-300 hover:text-[#1B2A4A]" onClick={() => setWalletDlg(w)} data-testid="edit-wallet-btn"><Pencil className="h-3.5 w-3.5" /></button>
                  <button className="text-gray-300 hover:text-red-500" onClick={() => delWallet(w.id)} data-testid="delete-wallet-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {w.over_100 && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t("ws_over100")}</p>}
                {(w.competitors || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {w.competitors.map((c, i) => (
                      <span key={i} className="text-[11px] bg-amber-50 text-amber-800 rounded px-1.5 py-0.5" title={c.source}>{c.name}: {c.share_pct}%</span>
                    ))}
                  </div>
                )}
                {w.notes && <p className="text-xs text-gray-500 mt-1">{w.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <SupplyDialog open={!!supplyDlg} onClose={() => setSupplyDlg(null)} plants={plants} initial={supplyDlg} accountId={accountId} onSaved={load} t={t} />
      <WalletDialog open={!!walletDlg} onClose={() => setWalletDlg(null)} initial={walletDlg} accountId={accountId} onSaved={load} t={t} />

      <Dialog open={!!histFor} onOpenChange={(o) => !o && setHistFor(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="wallet-history-dialog">
          <DialogHeader><DialogTitle>{t("ws_change_history")}</DialogTitle><DialogDescription>{histFor?.period}</DialogDescription></DialogHeader>
          <div className="space-y-2 text-sm">
            {(histFor?.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="border-b border-gray-50 pb-1.5">
                <p className="text-xs"><b>{h.user_name}</b> · {fmtDate(h.at)}</p>
                <p className="text-xs text-gray-500">{Object.keys(h.changes || {}).join(", ") || (h.changes?.action || "–")}</p>
              </div>
            ))}
            {!(histFor?.history || []).length && <p className="text-gray-400 text-xs">{t("no_history")}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
